import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  Row,
  Space,
  Upload,
  UploadFile,
  message,
} from "antd";
import { useForm } from "antd/es/form/Form";
import RobinPage from "../components/core/RobinPage";
import {
  useGetMyPartnerQuery,
  useUpdateMyPartnerMutation,
} from "../graphql/generated/hooks";
import { endpoint } from "../graphql";
import { InboxOutlined } from "@ant-design/icons";

const { Dragger } = Upload;

function Settings() {
  const { t, i18n } = useTranslation();
  const [form] = useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { data: partnerData, loading, refetch } = useGetMyPartnerQuery();
  const [updatePartner, { loading: updating }] = useUpdateMyPartnerMutation();
  const [editMode, setEditMode] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const onFinish = async (values: any) => {
    await updatePartner({
      variables: {
        address: values.address,
        email: values.email,
        phone: values.phone,
      },
    })
      .then(() => {
        messageApi.success(t("message.success.data_updated"));
      })
      .catch((error) => {
        messageApi.error(t("message.error.data_update_failed"));
        form.resetFields();
      });
    setEditMode(false);
    refetch();
  };

  useEffect(() => {
    form.resetFields();
    if (partnerData?.getPartner.image)
      setFileList([
        {
          uid: "-1",
          name: "image.png",
          status: "done",
          url: partnerData?.getPartner.image?.url,
          response: { id: partnerData?.getPartner.image?.id },
        },
      ]);
    // eslint-disable-next-line
  }, [partnerData]);

  return (
    <RobinPage title={t("title.general_settings")}>
      <Row gutter={[10, 10]}>
        {contextHolder}
        <Col xl={12} sm={24} style={{ padding: 50 }}>
          <Dragger
            maxCount={1}
            name="file"
            listType="picture-card"
            onRemove={async () => {
              setFileList([]);
              await updatePartner({
                variables: {
                  imageId: null,
                },
              });
            }}
            action={`${endpoint}/media/upload`}
            onChange={async ({ fileList: newFileList }) => {
              if (newFileList[0]?.status === "done") {
                message.success(
                  `${newFileList[0].name} file uploaded successfully`
                );
                await updatePartner({
                  variables: {
                    imageId: newFileList[0].response.id,
                  },
                });
              } else if (newFileList[0]?.status === "error") {
                message.error(`${newFileList[0].name} file upload failed.`);
              }
              setFileList(newFileList);
            }}
            fileList={fileList}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
            <p className="ant-upload-hint">
              Support for a single or bulk upload. Strictly prohibited from
              uploading company data or other banned files.
            </p>
          </Dragger>
          {/* <Upload>{fileList.length ? null : uploadButton}</Upload> */}
        </Col>
        <Col xl={12} sm={24}>
          <Form
            form={form}
            dir={i18n.dir()}
            name="login"
            initialValues={{ remember: true }}
            layout="vertical"
            disabled={!editMode}
            onFinish={onFinish}
            variant={editMode ? "outlined" : "borderless"}
            //   onFinishFailed={onFinishFailed}
          >
            <Form.Item
              wrapperCol={{ span: 12 }}
              name="email"
              initialValue={partnerData?.getPartner.email}
              rules={[
                {
                  required: true,
                  message: t("message.error.email_required"),
                  type: "email",
                },
              ]}
              label={t("label.email")}
            >
              <Input />
            </Form.Item>

            <Form.Item
              wrapperCol={{ span: 12 }}
              name="phone"
              rules={[
                {
                  required: true,
                  message: t("message.error.phone_required"),
                  type: "string",
                  len: 10,
                  pattern: /^05[0-9]*$/,
                },
              ]}
              initialValue={partnerData?.getPartner.phone}
              label={t("label.phone")}
            >
              <Input />
            </Form.Item>

            <Form.Item
              wrapperCol={{ span: 12 }}
              name="address"
              initialValue={partnerData?.getPartner.address}
              rules={[
                {
                  required: true,
                  message: t("message.error.address_required"),
                  type: "string",
                },
              ]}
              label={t("label.address")}
            >
              <Input />
            </Form.Item>

            <Form.Item hidden={!editMode}>
              <Space>
                <Button
                  onClick={() => {
                    setEditMode(false);
                    form.resetFields();
                  }}
                >
                  {t("button.cancel")}
                </Button>
                <Button loading={updating} type="primary" htmlType="submit">
                  {t("button.save")}
                </Button>
              </Space>
            </Form.Item>
          </Form>
          {!editMode && (
            <Button
              loading={loading}
              onClick={() => {
                setEditMode(true);
              }}
            >
              {t("button.edit")}
            </Button>
          )}
        </Col>
      </Row>
    </RobinPage>
  );
}

export default Settings;
